import { FC, useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { NotificationManager } from "react-notifications";
import BtButton from "../bt-button/bt-button";
import Modal from "../../components/shared/Modal/Modal";
import appState from "../../state/AppStateContainer";
import { serviceTenderWatch } from "../../services/service-tender-watch";
import { isUnchanged, SavedTenderWatch } from "./useSharedTenderFilter";
import styles from "./app-tender-watch-button.module.scss";

type Props = {
  areaIds: string[];
  places: string[];
  /**
   * Omitted by a tab with no CPV control. Leaving the key out is what tells the backend to
   * keep the codes already saved rather than treat the save as clearing them, and it keeps
   * this tab from deciding whether the CPV half has changed.
   */
  cpvCodes?: string[];
  /** The bevakning as last saved, or null when the user has none of their own. */
  saved: SavedTenderWatch | null;
  onSaved: (next: SavedTenderWatch) => void;
};

const AppTenderWatchButton: FC<Props> = ({ areaIds, places, cpvCodes, saved, onSaved }) => {
  const [t] = useTranslation("requests", { keyPrefix: "bevakning" });
  const [show, setShow] = useState(false);
  const [saving, setSaving] = useState(false);

  const hasFilter = areaIds.length > 0 || places.length > 0 || (cpvCodes?.length ?? 0) > 0;
  const unchanged = isUnchanged({ areaIds, places, cpvCodes }, saved);
  const canSave = hasFilter && !unchanged;
  const notifications = appState.getUserNotificationPreferences();

  // One bevakning covers both marketplaces, so saving opts the reader into the public half,
  // which is off by default. An unsubscribe from the private half was a deliberate act and is
  // pointed at rather than reversed.
  const privateIsMuted = notifications.weeklyDigest === false;

  // BtButton's disabled prop styles the button and blocks keyboard activation, but leaves
  // onClick wired; the guard has to be here or a disabled button still opens the dialog.
  const open = useCallback(() => {
    if (!canSave) return;
    setShow(true);
  }, [canSave]);

  const save = useCallback(async () => {
    // Same reason as open(): BtButton leaves onClick wired through disabled and loaderShow,
    // so a double click would send the PUT twice.
    if (saving) return;
    setSaving(true);
    try {
      const stored = await serviceTenderWatch.save({ areaIds, places, ...(cpvCodes ? { cpvCodes } : {}) });
      // Only when the reader has never expressed a preference, which is the same rule the
      // server applies - writing it unconditionally would show the toggle on to someone who
      // had deliberately switched it off.
      if (notifications.weeklyDigestPublic === undefined) {
        appState.setUserNotificationPreference("weeklyDigestPublic", true);
      }
      // From the response, not from the payload: the backend merges in the dimensions this
      // tab did not name, and the button has to compare against what is actually stored.
      onSaved({
        areaIds: (stored?.areaIds ?? areaIds).map(String),
        places: stored?.places ?? places,
        cpvCodes: stored?.cpvCodes ?? cpvCodes ?? [],
      });
      setShow(false);
      NotificationManager.success(t("notification.saved", "Bevakningen är sparad"));
    } catch (error) {
      NotificationManager.error(t("notification.saveError", "Kunde inte spara bevakningen"));
    } finally {
      setSaving(false);
    }
  }, [saving, notifications, areaIds, places, cpvCodes, onSaved, t]);

  const title = () => {
    if (!hasFilter) return t("emptyFilter", "Välj minst en kategori, region eller CPV-kod först");
    if (unchanged) return t("noChanges", "Filtreringen är redan sparad som din bevakning");
    return undefined;
  };

  return (
    <>
      <BtButton
        color="white"
        disabled={!canSave}
        onClick={open}
        title={title()}
      >
        {saved
          ? t("button.update", "Uppdatera bevakningen")
          : t("button.save", "Bevaka den här filtreringen")}
      </BtButton>

      <Modal
        show={show}
        setShow={setShow}
        unmountWhenHidden
        title={saved
          ? t("modal.updateTitle", "Uppdatera bevakningen")
          : t("modal.title", "Bevaka den här filtreringen")}
        buttonInfo={{
          label: t("modal.confirm", "Spara bevakning"),
          action: save,
          loaderShow: saving,
        }}
      >
        <p className={styles.intro}>
          {t(
            "modal.intro",
            "Bevakningen gäller både förfrågningar på marknadsplatsen och offentliga upphandlingar. Du får ett mail varje onsdag med det som matchar.",
          )}
        </p>

        {saved && (
          <p className={styles.note}>
            {t("modal.replaces", "Du har en bevakning per organisation, så den här ersätter din nuvarande.")}
          </p>
        )}

        {privateIsMuted && (
          <p className={styles.warning}>
            {t(
              "modal.privateMuted",
              "Du har stängt av veckomailet för förfrågningar. Slå på det igen under Notifikationer på din profil för att få den delen av bevakningen.",
            )}
          </p>
        )}
      </Modal>
    </>
  );
};

export default AppTenderWatchButton;
